import { getServerBaseUrl } from './serverUrl'

export const GUEST_OFFICE_NOT_OPEN_ERROR = 'The meeting office is not open yet'

/**
 * The room lookup answers a guest with one generic 401 for any bad token, so the specific reason
 * (cancelled, removed, expired, company closed) is lost. These are the texts the server's own
 * join refusal uses; `guestJoinFailureReason` (utils/guestFarewellUrl.ts) maps each to the same
 * farewell as before, so keep them in step with it.
 */
const GUEST_REFUSAL_MESSAGES: Record<string, string> = {
  cancelled: 'Meeting has been cancelled',
  expired: 'Meeting token has expired',
  removed: 'You are no longer on the invite list for this meeting',
  unavailable: 'This meeting is currently unavailable.',
}

/**
 * A guest may only join the office's running Colyseus room, never create one: a guest-created
 * instance would be a second copy of the office with nobody from the company in it.
 * `code: 404` makes the pipeline treat it as ROOM_NOT_FOUND (not retried automatically).
 */
export function guestJoinRoomId(resolved: { roomId: string } | null | undefined): string {
  if (!resolved?.roomId) {
    throw Object.assign(new Error(GUEST_OFFICE_NOT_OPEN_ERROR), { code: 404 })
  }
  return resolved.roomId
}

/**
 * This check runs inside the 15 s JOINING_ROOM stage, after the room lookup (up to 10 s). A slow
 * answer must not use up the stage's time, or the guest sees "We couldn't connect you" instead of
 * the cancelled or removed farewell; giving up falls back to "office not open".
 */
const GUEST_REFUSAL_CHECK_TIMEOUT_MS = 4000

/**
 * Asks the join-page endpoint once why the guest's token was refused. Null for a valid token, an
 * unknown token or any failure of the call itself (including the timeout), which all keep "office
 * not open".
 */
async function guestRefusalMessage(meetingToken: string): Promise<string | null> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), GUEST_REFUSAL_CHECK_TIMEOUT_MS)
  try {
    const resp = await fetch(`${getServerBaseUrl()}/api/guest/validate-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: meetingToken }),
      signal: controller.signal,
    })
    const data = await resp.json()
    if (data?.valid !== false) return null
    return GUEST_REFUSAL_MESSAGES[data.error] ?? null
  } catch {
    return null
  } finally {
    clearTimeout(timeoutId)
  }
}

/**
 * The room to join for a guest. When the lookup found nothing, the token may have been cancelled,
 * removed or expired since the join page loaded: throw the matching refusal (code 403, like the
 * server's join refusal) so the guest sees that farewell, not "office not open".
 */
export async function resolveGuestJoinRoomId(
  resolved: { roomId: string } | null | undefined,
  meetingToken: string
): Promise<string> {
  if (resolved?.roomId) return resolved.roomId
  const refusal = await guestRefusalMessage(meetingToken)
  if (refusal) throw Object.assign(new Error(refusal), { code: 403 })
  return guestJoinRoomId(resolved)
}
